/**
 * 用量反応（LD50/EC50/EC90/TCID50）結果を ResultCache に変換するアダプター
 * Phase 3: 既存の calculateDoseResponse / calculateTCID50 を呼び出し、
 * 右ペインとグラフ用の ResultCache を返す。
 */

import {
  calculateDoseResponse,
  calculateTCID50,
  type DoseResponseResult,
  type Method,
  type TCID50Method,
} from '../statistics/doseResponse';
import type { DilutionData } from '../statistics/tcid50';
import { normalCDF } from '../statistics/probit';
import type { DataRow } from '../../stores/systems/keisuke/dashboardStore';
import type { AnalysisOptions, ResultCache, VariableMapping } from '../../stores/systems/keisuke/dashboardStore';
import {
  drMethodUsesApproximateInterval,
  normalizeDrMethodForType,
  normalizeDrType,
} from '../keisukeDoseResponseDefaults';
import { parseFiniteNumberFromCell } from '../keisukeGridData';
import { kt, KeisukeAdapterError } from '../keisukeAdapterI18n';

/** 論文用の科学記数法（例: 1.7 × 10²）。レガシー DoseResponseView と同様 */
function formatScientificNotation(value: number, precision: number = 2): string {
  if (value === 0 || !Number.isFinite(value)) return value.toFixed(precision);
  const magnitude = Math.floor(Math.log10(Math.abs(value)));
  const coefficient = value / Math.pow(10, magnitude);
  const rounded =
    Math.round(coefficient * Math.pow(10, precision - 1)) / Math.pow(10, precision - 1);
  if (magnitude === 0) return rounded.toFixed(precision);
  const sup = magnitude.toString().replace(/-/g, '⁻').replace(/\d/g, (d) =>
    '⁰¹²³⁴⁵⁶⁷⁸⁹'[Number(d)]);
  return `${rounded.toFixed(Math.max(0, precision - 1))} × 10${sup}`;
}

const APPROX_INTERVAL_METHODS_NOTE =
  'An approximate interval based on half the average log-dose (or log-dilution) step was computed for reference; this is not a formal 95% confidence interval.';

function formatApproxIntervalResults(
  valueLabel: string,
  valStr: string,
  lower: number,
  upper: number
): string {
  return (
    `The estimated ${valueLabel} was ${valStr} units. ` +
    `An approximate interval (based on log spacing) was ${formatScientificNotation(lower, 2)} – ${formatScientificNotation(upper, 2)}; this is not a formal 95% confidence interval.`
  );
}

/** レガシー計助 DoseResponseView と同様の論文用 Methods + Results 英文（LD50/EC50/EC90） */
function buildDoseResponsePaperText(
  valueLabel: string,
  result: DoseResponseResult,
  method: Method
): string {
  const valStr = formatScientificNotation(result.value, 2);
  const ci = result.confidenceInterval;
  const ciStr =
    ci && Number.isFinite(ci.lower) && Number.isFinite(ci.upper)
      ? ` (95% CI: ${formatScientificNotation(ci.lower, 2)} – ${formatScientificNotation(ci.upper, 2)})`
      : '';

  // ANOVA と同様に Results は「要約 → 統計的結論」の流れで記載
  const resultsWithCI = ciStr
    ? `The estimated ${valueLabel} was ${valStr} units. The 95% confidence interval was ${formatScientificNotation(ci!.lower, 2)} – ${formatScientificNotation(ci!.upper, 2)}.`
    : `The estimated ${valueLabel} was ${valStr} units.`;

  if (method === 'probit' && result.probitResult) {
    const { intercept, slope } = result.probitResult;
    const methods = `The ${valueLabel} was estimated by probit analysis using a generalized linear model with a binomial distribution and a probit link function. Model parameters were estimated by maximum likelihood (β₀ = ${intercept.toFixed(6)}, β₁ = ${slope.toFixed(6)}). The ${valueLabel} and its 95% confidence interval were obtained directly from the fitted model using profile likelihood.`;
    return `Methods\n\n${methods}\n\nResults\n\n${resultsWithCI}`;
  }

  if (method === 'logistic' && result.logisticResult) {
    const { intercept, slope } = result.logisticResult;
    const methods = `The ${valueLabel} was estimated by logistic regression using a generalized linear model with a binomial distribution and a logit link function. Model parameters were estimated by maximum likelihood (β₀ = ${intercept.toFixed(6)}, β₁ = ${slope.toFixed(6)}). The ${valueLabel} and its 95% confidence interval were obtained using Wald (delta method) intervals based on the fitted GLM.`;
    return `Methods\n\n${methods}\n\nResults\n\n${resultsWithCI}`;
  }

  if (method === 'reed-muench' || method === 'spearman-karber') {
    const methodName = method === 'reed-muench' ? 'Reed-Muench' : 'Spearman-Karber';
    const targetRate = valueLabel === 'EC90' ? 0.9 : 0.5;
    const methods =
      `The ${valueLabel} was estimated using the ${methodName} method. The ${valueLabel} was determined by linear interpolation between two consecutive dose levels that bracketed a ${targetRate * 100}% response on the logarithmic dose scale. ${APPROX_INTERVAL_METHODS_NOTE}`;
    const results =
      ci && Number.isFinite(ci.lower) && Number.isFinite(ci.upper)
        ? formatApproxIntervalResults(valueLabel, valStr, ci.lower, ci.upper)
        : `The estimated ${valueLabel} was ${valStr} units.`;
    return `Methods\n\n${methods}\n\nResults\n\n${results}`;
  }

  const methods = `The ${valueLabel} was estimated by ${method} method.`;
  return `Methods\n\n${methods}\n\nResults\n\n${resultsWithCI}`;
}

/** レガシー計助 DoseResponseView と同様の論文用 Methods + Results 英文（TCID50）。ANOVA 同様に Results は要約＋結論の流れで記載 */
function buildTCID50PaperText(
  result: { value: number; method: string; confidenceInterval?: { lower?: number; upper?: number } }
): string {
  const formattedValue = formatScientificNotation(result.value, 2);
  const ci = result.confidenceInterval;
  const hasCI = ci && Number.isFinite(ci.lower) && Number.isFinite(ci.upper);
  const methods =
    `The TCID50 was estimated using the ${result.method} method. The TCID50 was determined by linear interpolation between two consecutive dilution levels that bracketed a 50% infection rate on the logarithmic dilution scale. ${APPROX_INTERVAL_METHODS_NOTE}`;
  const results = hasCI
    ? formatApproxIntervalResults('TCID50', formattedValue, ci!.lower!, ci!.upper!)
    : `The estimated TCID50 was ${formattedValue}.`;
  return `Methods\n\n${methods}\n\nResults\n\n${results}`;
}

function buildCurvePoints(
  doses: number[],
  result: DoseResponseResult
): [number, number][] {
  const minD = Math.min(...doses);
  const maxD = Math.max(...doses);
  const logMin = Math.log10(minD);
  const logMax = Math.log10(maxD);
  const n = 50;
  const points: [number, number][] = [];

  if (result.probitResult) {
    const { slope, intercept } = result.probitResult;
    for (let i = 0; i <= n; i++) {
      const logD = logMin + (i / n) * (logMax - logMin);
      const dose = Math.pow(10, logD);
      const probit = intercept + slope * logD;
      const p = normalCDF(probit);
      points.push([dose, Math.max(0, Math.min(100, p * 100))]);
    }
  } else if (result.logisticResult) {
    const { slope, intercept } = result.logisticResult;
    for (let i = 0; i <= n; i++) {
      const logD = logMin + (i / n) * (logMax - logMin);
      const dose = Math.pow(10, logD);
      const logit = intercept + slope * logD;
      const p = 1 / (1 + Math.exp(-logit));
      points.push([dose, Math.max(0, Math.min(100, p * 100))]);
    }
  }
  return points;
}

/** 非 GLM 手法向け: 観測 (dose, rate%) を log 用量軸で折れ線補間した曲線点 */
export function buildObservedLogDoseLineCurve(
  doses: number[],
  responseRates: number[]
): [number, number][] {
  if (doses.length < 2 || responseRates.length !== doses.length) return [];
  const sorted = doses
    .map((dose, i) => ({ dose, rate: responseRates[i]! * 100 }))
    .filter((p) => p.dose > 0 && Number.isFinite(p.dose))
    .sort((a, b) => a.dose - b.dose);
  if (sorted.length < 2) return [];
  const logMin = Math.log10(sorted[0]!.dose);
  const logMax = Math.log10(sorted[sorted.length - 1]!.dose);
  const n = 50;
  const points: [number, number][] = [];
  for (let i = 0; i <= n; i++) {
    const logD = logMin + (i / n) * (logMax - logMin);
    const dose = Math.pow(10, logD);
    let rate = sorted[0]!.rate;
    for (let j = 0; j < sorted.length - 1; j++) {
      const d0 = sorted[j]!.dose;
      const d1 = sorted[j + 1]!.dose;
      if (dose <= d0) {
        rate = sorted[j]!.rate;
        break;
      }
      if (dose >= d1) {
        rate = sorted[j + 1]!.rate;
        continue;
      }
      const log0 = Math.log10(d0);
      const log1 = Math.log10(d1);
      const t = log1 === log0 ? 0 : (logD - log0) / (log1 - log0);
      rate = sorted[j]!.rate + t * (sorted[j + 1]!.rate - sorted[j]!.rate);
      break;
    }
    points.push([dose, Math.max(0, Math.min(100, rate))]);
  }
  return points;
}

export function runDoseResponseAdapter(
  gridData: DataRow[],
  options: AnalysisOptions
): ResultCache {
  const mapping: VariableMapping = options.mapping ?? {};
  const doseCol = mapping.doseColumn;
  const effectCol = mapping.effectColumn;
  const totalCol = mapping.totalColumn;
  const drType = normalizeDrType(options.drType);
  const method = normalizeDrMethodForType(drType, options.drMethod);
  if (!doseCol) throw new KeisukeAdapterError('doseColumnRequired');
  if (!effectCol) throw new KeisukeAdapterError('effectColumnRequired');
  if (gridData.length === 0) throw new KeisukeAdapterError('dataRequired');
  if (drType !== 'tcid50' && (method === 'probit' || method === 'logistic') && !totalCol) {
    throw new KeisukeAdapterError('glmTotalColumnRequired');
  }

  if (drType === 'tcid50') {
    if (!totalCol) throw new KeisukeAdapterError('totalColumnRequired');
    const dilutionData: DilutionData[] = [];
    for (const row of gridData) {
      const doseNum = parseFiniteNumberFromCell(row[doseCol]);
      const infNum = parseFiniteNumberFromCell(row[effectCol]);
      const totNum = parseFiniteNumberFromCell(row[totalCol]);
      if (doseNum === null || infNum === null || totNum === null || totNum <= 0) continue;
      dilutionData.push({
        dilution: doseNum,
        infected: infNum,
        total: totNum,
        infectionRate: infNum / totNum,
      });
    }
    if (dilutionData.length < 2) throw new KeisukeAdapterError('minTwoPoints');
    const result = calculateTCID50(
      dilutionData,
      (method === 'spearman-karber' ? 'spearman-karber' : 'reed-muench') as TCID50Method
    );
    const observed: [number, number][] = dilutionData.map((d) => [d.dilution, d.infectionRate * 100]);
    const methodLabel =
      result.method === 'spearman-karber'
        ? kt('statsDetail.doseResponse.methodSpearmanKarber')
        : kt('statsDetail.doseResponse.methodReedMuench');
    const statsDetailText = [
      kt('statsDetail.overviewTitle'),
      kt('statsDetail.doseResponse.tcid50Intro', { method: methodLabel }),
      kt('statsDetail.doseResponse.tcid50Estimate', {
        value: result.value,
        log10: result.valueLog10?.toFixed(4) ?? '—',
      }),
      result.confidenceInterval?.lower != null && result.confidenceInterval?.upper != null
        ? kt('statsDetail.doseResponse.approxIntervalLine', {
            label: 'TCID50',
            value: result.value,
            lower: result.confidenceInterval.lower,
            upper: result.confidenceInterval.upper,
          })
        : '',
      kt('statsDetail.formulasRef'),
    ]
      .filter(Boolean)
      .join('\n');

    return {
      stats: {
        TCID50: result.value,
        'TCID50 (log10)': result.valueLog10,
        method: result.method,
        'Approx. interval lower': result.confidenceInterval?.lower,
        'Approx. interval upper': result.confidenceInterval?.upper,
      },
      paperText: buildTCID50PaperText(result),
      statsDetailText,
      doseResponseChart: { observed },
      rawResult: { ...result, dilutionData, method: result.method },
      updatedAt: Date.now(),
    };
  }

  const type = drType as 'ld50' | 'ec50' | 'ec90';
  const doses: number[] = [];
  const responses: number[] = [];
  const dead: number[] = [];
  const total: number[] = [];

  for (const row of gridData) {
    const t = totalCol ? row[totalCol] : undefined;
    const doseNum = parseFiniteNumberFromCell(row[doseCol]);
    const effectNum = parseFiniteNumberFromCell(row[effectCol]);
    if (doseNum === null || effectNum === null) continue;
    if (t != null && totalCol) {
      const totNum = parseFiniteNumberFromCell(t);
      if (totNum === null || totNum <= 0) continue;
      doses.push(doseNum);
      dead.push(effectNum);
      total.push(totNum);
      responses.push(effectNum / totNum);
    } else {
      const r = effectNum <= 1 ? effectNum : effectNum / 100;
      if (r < 0 || r > 1) continue;
      doses.push(doseNum);
      responses.push(r);
    }
  }

  if (doses.length < 2) throw new KeisukeAdapterError('minTwoPoints');
  const useDeadTotal = (method === 'probit' || method === 'logistic') && dead.length === doses.length && total.length === doses.length;
  const result: DoseResponseResult = useDeadTotal
    ? calculateDoseResponse(type, doses, responses, method, dead, total)
    : calculateDoseResponse(type, doses, responses, method);

  const observed: [number, number][] = doses.map((d, i) => [d, responses[i] * 100]);
  const glmCurve = buildCurvePoints(doses, result);
  const curve =
    glmCurve.length > 0 ? glmCurve : buildObservedLogDoseLineCurve(doses, responses);

  const valueLabel = type === 'ec90' ? 'EC90' : type === 'ec50' ? 'EC50' : 'LD50';
  const usesApproxInterval = drMethodUsesApproximateInterval(method);

  const stats: Record<string, unknown> = {
    [valueLabel]: result.value,
    method: result.method,
  };
  if (usesApproxInterval) {
    stats['Approx. interval lower'] = result.confidenceInterval?.lower;
    stats['Approx. interval upper'] = result.confidenceInterval?.upper;
  } else {
    stats['95%CI lower'] = result.confidenceInterval?.lower;
    stats['95%CI upper'] = result.confidenceInterval?.upper;
  }
  if (result.probitResult) {
    stats['slope (probit)'] = result.probitResult.slope;
    stats['intercept (probit)'] = result.probitResult.intercept;
  }
  if (result.logisticResult) {
    stats['slope (logistic)'] = result.logisticResult.slope;
    stats['intercept (logistic)'] = result.logisticResult.intercept;
  }

  const methodKey =
    method === 'probit'
      ? 'statsDetail.doseResponse.methodProbit'
      : method === 'logistic'
        ? 'statsDetail.doseResponse.methodLogistic'
        : method === 'reed-muench'
          ? 'statsDetail.doseResponse.methodReedMuench'
          : 'statsDetail.doseResponse.methodSpearmanKarber';
  const ciLo = result.confidenceInterval?.lower;
  const ciHi = result.confidenceInterval?.upper;
  const estimateLineKey = usesApproxInterval
    ? 'statsDetail.doseResponse.approxIntervalLine'
    : 'statsDetail.doseResponse.estimateLine';
  const statsDetailText = [
    kt('statsDetail.overviewTitle'),
    kt('statsDetail.doseResponse.estimateIntro', {
      label: valueLabel,
      method: kt(methodKey),
    }),
    kt(estimateLineKey, {
      label: valueLabel,
      value: result.value,
      lower: ciLo ?? '—',
      upper: ciHi ?? '—',
    }),
    kt('statsDetail.formulasRef'),
  ].join('\n');

  return {
    stats,
    paperText: buildDoseResponsePaperText(valueLabel, result, method),
    statsDetailText,
    doseResponseChart: { observed, curve },
    rawResult: { ...result, doses, responses },
    updatedAt: Date.now(),
  };
}
